/**
 * @file Frontend API client — manufacturingProcessesAdminService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";

/** Type definition — Manufacturing Process Material Ref. */
export interface ManufacturingProcessMaterialRef {
  id: number;
  code: string;
  name: string;
}

/** Type definition — Manufacturing Process Admin. */
export interface ManufacturingProcessAdmin {
  id: number;
  materialId: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  material?: ManufacturingProcessMaterialRef;
}

/** Type definition — Manufacturing Process Create Input. */
export interface ManufacturingProcessCreateInput {
  materialId: number;
  name: string;
  isActive?: boolean;
}

/** Type definition — Manufacturing Process Update Input. */
export interface ManufacturingProcessUpdateInput {
  materialId?: number;
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
}

function apiMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Request failed"
  );
}

/** Exported const — manufacturing Processes Admin Service. */
export const manufacturingProcessesAdminService = {
  async list(materialId?: number): Promise<ManufacturingProcessAdmin[]> {
    try {
      const params: Record<string, number> = {};
      if (materialId != null && Number.isFinite(materialId) && materialId > 0) {
        params.materialId = materialId;
      }
      const res = await axiosClient.get<{
        success?: boolean;
        data?: ManufacturingProcessAdmin[];
      }>(API_URLS.ADMIN.MANUFACTURING_PROCESSES.LIST, {
        params: Object.keys(params).length ? params : undefined,
      });
      if (res.data?.success === false) {
        throw new Error(
          (res.data as { message?: string }).message ||
            "Failed to list manufacturing processes"
        );
      }
      const rows = res.data?.data;
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async getOne(id: number): Promise<ManufacturingProcessAdmin> {
    try {
      const res = await axiosClient.get<{
        success?: boolean;
        data?: ManufacturingProcessAdmin;
      }>(API_URLS.ADMIN.MANUFACTURING_PROCESSES.BY_ID(id));
      if (res.data?.success === false || !res.data?.data) {
        throw new Error(
          (res.data as { message?: string })?.message || "Not found"
        );
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async create(
    body: ManufacturingProcessCreateInput
  ): Promise<ManufacturingProcessAdmin> {
    try {
      const res = await axiosClient.post<{
        success?: boolean;
        data?: ManufacturingProcessAdmin;
      }>(API_URLS.ADMIN.MANUFACTURING_PROCESSES.LIST, body);
      if (res.data?.success === false || !res.data?.data) {
        throw new Error(
          (res.data as { message?: string })?.message || "Create failed"
        );
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async update(
    id: number,
    body: ManufacturingProcessUpdateInput
  ): Promise<ManufacturingProcessAdmin> {
    try {
      const res = await axiosClient.put<{
        success?: boolean;
        data?: ManufacturingProcessAdmin;
      }>(API_URLS.ADMIN.MANUFACTURING_PROCESSES.BY_ID(id), body);
      if (res.data?.success === false || !res.data?.data) {
        throw new Error(
          (res.data as { message?: string })?.message || "Update failed"
        );
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async delete(id: number): Promise<void> {
    try {
      const res = await axiosClient.delete<{
        success?: boolean;
        message?: string;
      }>(API_URLS.ADMIN.MANUFACTURING_PROCESSES.BY_ID(id));
      if (res.data?.success === false) {
        throw new Error(res.data.message || "Delete failed");
      }
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },
};
